"use client";

import type { ReactNode } from "react";

import { cx } from "@tripcart/traveler-ui/cx";

import { useMediaGallery } from "@/components/experience-media-gallery";

import styles from "./gallery-tile.module.css";

/**
 * Phase 1D (perf/lighthouse-100): tiny client island for the bento-grid
 * tile buttons. Lets the surrounding hero render as a Server Component
 * while keeping the "click → open gallery" interaction.
 *
 * Receives the server-rendered <Image> (or whatever) as `children` —
 * children prop crosses the server/client boundary as plain React tree
 * elements, so no extra JS ships for the image markup itself.
 */
interface GalleryTileProps {
  /** Open by URL — preferred. Falls back to `index` when omitted. */
  url?: string;
  /** Open by index (for the "no specific image" case, e.g. the 0-image
   *  placeholder). Ignored when `url` is set. */
  index?: number;
  ariaLabel: string;
  className?: string;
  /** "tile" — visible on every viewport. "tile-desktop" — hidden below
   *  `md`, used for supplementary thumbs in the bento grid. Defaults to
   *  "tile". */
  variant?: "tile" | "tile-desktop";
  children: ReactNode;
}

export function GalleryTile({
  url,
  index = 0,
  ariaLabel,
  className,
  variant = "tile",
  children,
}: GalleryTileProps) {
  const { openByIndex, openByUrl, prewarm } = useMediaGallery();
  return (
    <button
      type="button"
      aria-label={ariaLabel}
      // No dark overlay — the reference keeps every cell clean; the image's
      // hover-zoom (on the child <Image>) is the affordance. Radius comes from
      // the caller (full tile) or the grid container that clips it.
      className={cx(
        styles.tile,
        variant === "tile-desktop" && styles.desktopOnly,
        className,
      )}
      // Warm the lightbox's first image + filmstrip on hover/focus intent so it
      // opens already painted.
      onPointerEnter={prewarm}
      onFocus={prewarm}
      onClick={() => (url ? openByUrl(url) : openByIndex(index))}
    >
      {/* Skeleton shimmer sits BEHIND the image. The <Image> (fill,
          object-cover, opaque) renders after it in DOM order and fully covers
          it once painted, so the pulse only shows while loading. Keeping the
          pulse off the button stops the loaded image from inheriting the
          animated opacity and breathing forever. */}
      <span aria-hidden className={styles.shimmer} />
      {children}
    </button>
  );
}
